// AI-CONTEXT: See docs/features/platform-mrr-card.md
// AI-CONTEXT: See docs/patterns.md#dashboard-static-card
import { useHistory } from 'react-router-dom'
import { client } from '../../hooks/Client'
import usePlatformRevenue from '../../hooks/usePlatformRevenue'
import mrrDisplay from '../../helpers/dashboard/mrrDisplay'
import buildPlatformStatSeries from '../../helpers/dashboard/buildPlatformStatSeries'
import toPercentSeries from '../../helpers/dashboard/toPercentSeries'
import formatPercentChange from '../../helpers/formatPercentChange'
import formatMonthLabel from '../../helpers/formatMonthLabel'
import StaticCardLabel from './StaticCardLabel'
import KpiValue from './KpiValue'

/**
 * KPI-tegel met de maandelijks terugkerende omzet (Deccos-beheertenant).
 *
 * Eigen component en niet DashboardPlatformStatCard: die leest PlatformStats
 * rechtstreeks uit Firestore, en omzet mag niet in een collectie staan die elke
 * ingelogde klant kan lezen. De cijfers komen hier via een afgeschermde
 * callable.
 *
 * Onder het bedrag staat wat er NIET in zit: hoeveel organisaties nog geen
 * bedrag hebben en hoeveel periodes verlopen zijn. Zonder die regel leest een
 * onvolledige administratie als een lagere omzet.
 *
 * De knop onderaan opent het abonnementenoverzicht — de werklijst achter dat
 * getal. Bewust een eigen knop en niet de hele tegel klikbaar: de tegel zit in
 * een sleepbaar raster, en een klik op het geheel vecht met het slepen.
 *
 * Neemt geen props: anders dan DashboardPlatformStatCard volgt de metric hier
 * niet uit een DashboardItems-Type — deze kaart toont er maar één.
 */
const DashboardMrrCard = () => {
    const routerHistory = useHistory()
    const { current, history, loading, error } = usePlatformRevenue()
    const display = mrrDisplay(current)

    const percentSeries = toPercentSeries(buildPlatformStatSeries(history, 'MrrCents'))
    const change = percentSeries.length ? percentSeries[percentSeries.length - 1] : null
    const since = percentSeries.length ? formatMonthLabel(percentSeries[0].month) : ''

    return (
        <div className='participation-value-card'>
            <StaticCardLabel title='Maandelijkse omzet' pill={{ variant: 'msi', label: 'Deccos' }} />

            {/* Laden krijgt een eigen weergave: een streepje beweert 'geen
                omzet' en dat is onwaar zolang de callable nog loopt.
                AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading */}
            {/* Het bedrag opent het verloop over de tijd (totaal en per sector).
                Een knop op het bedrag en niet de hele tegel: de tegel zit in een
                sleepbaar raster. Spec: docs/features/platform-revenue-churn-trend.md */}
            <KpiValue
                loading={loading}
                value={display.value}
                onClick={() => routerHistory.push(`/${client}/platformRevenue`)}
                ariaLabel='Omzet over de tijd'
            />

            {/* De fout tonen, niet wegslikken: een streepje zonder uitleg leest
                als 'nog geen omzet' terwijl de oorzaak een mislukte aanroep is. */}
            {error && !loading && (
                <p className='participation-value-note participation-value-note--error'>{error}</p>
            )}


            {change && (
                <p className={`participation-value-change ${change.value > 0 ? 'pos' : change.value < 0 ? 'neg' : 'neutral'}`}>
                    {formatPercentChange(change.value)}
                    <span className='participation-value-change-since'>{` sinds ${since}`}</span>
                </p>
            )}

            {/* Eén regel, geen twee: de KPI-tegel heeft een vaste hoogte en een
                losse knop eronder valt eruit. Het voorbehoud beschrijft het werk
                al ("94 organisaties zonder bedrag"), dus dat is precies waar je
                op wilt klikken. Zonder voorbehoud een neutrale tekst.
                Het aria-label blijft gelijk, zodat de knop vindbaar is ongeacht
                wat erop staat. */}
            <button
                type='button'
                className='participation-value-link'
                aria-label='Alle abonnementen'
                onClick={() => routerHistory.push(`/${client}/subscriptionsOverview`)}
            >
                {display.note ? `${display.note} →` : 'Alle abonnementen →'}
            </button>
        </div>
    )
}

export default DashboardMrrCard
